/**
 * Customer Favorites Page
 *
 * Displays customer's favorite guides and experiences with quick actions.
 * Allows viewing and managing favorites (unfavorite functionality).
 *
 * Epic: MVP 1.21.4 - Customer Dashboard
 * Story: US-1.21.4-13: Favorite Guides (3 SP)
 * Story: US-1.21.4-14: Favorite Experiences (3 SP)
 *
 * Data Flow:
 * - Fetches from GET /api/v1/customer/favorites/guides
 * - Fetches from GET /api/v1/customer/favorites/experiences
 * - Deletes via DELETE /api/v1/customer/favorites/:type/:id
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiRequest from '../../services/api';
import { logError } from '../../services/errorLoggingService';
import { formatCentsToDollars } from '../../utils/formHelpers';
import {
  Heart,
  Star as _Star,
  MapPin as _MapPin,
  MessageSquare,
  DollarSign,
  Calendar,
  User as _User,
  AlertCircle as _AlertCircle,
  HeartOff as _HeartOff
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import EmptyState from '../../components/common/EmptyState';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorAlert from '../../components/common/ErrorAlert';
import Avatar from '../../components/common/Avatar';
import ConfirmModal from '../../components/shared/ConfirmModal';
import { getGuidePhotoUrl, getGuidePhotoSrcSet } from '../../utils/guideDataAdapters';
import { buildSrcSet, getResponsiveSizes } from '../../services/assetService';

export default function CustomerFavorites() {
  const navigate = useNavigate();
  const [favoriteGuides, setFavoriteGuides] = useState([]);
  const [favoriteExperiences, setFavoriteExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('guides'); // guides, experiences
  const [removingId, setRemovingId] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, type: null, id: null });
  const [errorMessage, setErrorMessage] = useState(null);

  // SCRUM-836: Experience favorites backend is built
  const EXPERIENCE_FAVORITES_ENABLED = true;

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      setLoading(true);
      const [guidesResponse, experiencesResponse] = await Promise.all([
        apiRequest('/api/v1/customer/favorites/guides', { method: 'GET' }),
        apiRequest('/api/v1/customer/favorites/experiences', { method: 'GET' })
      ]);

      if (guidesResponse.success) {
        setFavoriteGuides(guidesResponse.data);
      }
      if (experiencesResponse.success) {
        setFavoriteExperiences(experiencesResponse.data);
      }
    } catch (err) {
      if (err.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError(err.message || 'Failed to load favorites');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFavorite = async (type, id) => {
    try {
      setRemovingId(id);
      setErrorMessage(null);
      const response = await apiRequest(`/api/v1/customer/favorites/${type}/${id}`, { method: 'DELETE' });

      if (response.success) {
        // Update local state
        if (type === 'guides') {
          setFavoriteGuides(prev => prev.filter(g => g.guide_id !== id));
        } else {
          setFavoriteExperiences(prev => prev.filter(e => e.experience_id !== id));
        }
        setConfirmModal({ isOpen: false, type: null, id: null });
      }
    } catch (err) {
      logError(err, {
        componentName: 'CustomerFavorites',
        action: 'remove_favorite',
        type,
        id
      });
      if (err.status === 401) {
        setErrorMessage('Session expired. Please log in again.');
      } else {
        setErrorMessage(err.message || 'Failed to remove favorite. Please try again.');
      }
      setTimeout(() => setErrorMessage(null), 5000);
      setConfirmModal({ isOpen: false, type: null, id: null });
    } finally {
      setRemovingId(null);
    }
  };

  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  if (loading) {
    return (
      <DashboardLayout userRole="customer" activeNavId="favorites" onNavigate={handleNavigate}>
        <LoadingSpinner message="Loading favorites..." className="min-h-screen" />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout userRole="customer" activeNavId="favorites" onNavigate={handleNavigate}>
        <ErrorAlert message={error} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      userRole="customer"
      activeNavId="favorites"
      onNavigate={handleNavigate}
    >
      <div className="space-y-6">
        {/* Error Messages */}
        {errorMessage && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg flex items-center space-x-2">
            <_AlertCircle className="w-5 h-5" />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Favorites</h1>
          <p className="mt-1 text-sm text-gray-500">
            Your saved guides and experiences for easy access
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8" role="tablist" aria-label="Favorite categories">
            <button
              onClick={() => setActiveTab('guides')}
              role="tab"
              aria-selected={activeTab === 'guides'}
              aria-controls="guides-panel"
              id="guides-tab"
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                activeTab === 'guides'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Favorite Guides
              {favoriteGuides.length > 0 && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {favoriteGuides.length}
                </span>
              )}
            </button>
            {EXPERIENCE_FAVORITES_ENABLED && (
              <button
                onClick={() => setActiveTab('experiences')}
                role="tab"
                aria-selected={activeTab === 'experiences'}
                aria-controls="experiences-panel"
                id="experiences-tab"
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  activeTab === 'experiences'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Favorite Experiences
                {favoriteExperiences.length > 0 && (
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {favoriteExperiences.length}
                  </span>
                )}
              </button>
            )}
          </nav>
        </div>

        {/* Favorite Guides Tab */}
        {activeTab === 'guides' && (
          <div
            role="tabpanel"
            id="guides-panel"
            aria-labelledby="guides-tab"
          >
            {favoriteGuides.length === 0 ? (
              <EmptyState
                icon={Heart}
                title="No Favorite Guides Yet"
                description="Start exploring and save guides you'd like to book with"
                action={
                  <Button onClick={() => navigate('/')}>
                    Browse Guides
                  </Button>
                }
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {favoriteGuides.map((guide) => (
                  <div
                    key={guide.guide_id}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                  >
                    {/* Guide Header */}
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <Avatar
                            src={getGuidePhotoUrl(guide, 'profile', 'thumbnail')}
                            srcSet={getGuidePhotoSrcSet(guide, 'profile') || undefined}
                            sizes={getGuidePhotoSrcSet(guide, 'profile') ? '48px' : undefined}
                            name={guide.guide_name}
                            size="lg"
                          />
                          <div>
                            <h3 className="font-semibold text-gray-900">{guide.guide_name}</h3>
                            {guide.specialties && (
                              <p className="text-sm text-gray-600">{guide.specialties.split(',')[0]}</p>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => setConfirmModal({ isOpen: true, type: 'guides', id: guide.guide_id })}
                          disabled={removingId === guide.guide_id}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                          title="Remove from favorites"
                        >
                          {removingId === guide.guide_id ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-500"></div>
                          ) : (
                            <Heart className="w-5 h-5 fill-current" />
                          )}
                        </button>
                      </div>

                      {/* Guide Stats */}
                      <div className="space-y-2 mb-4">
                        {guide.average_rating && (
                          <div className="flex items-center text-sm text-gray-600">
                            <_Star className="w-4 h-4 text-yellow-400 fill-current mr-1" />
                            <span className="font-medium text-gray-900">{guide.average_rating.toFixed(1)}</span>
                            <span className="mx-1">·</span>
                            <span>{guide.total_reviews} reviews</span>
                          </div>
                        )}
                        {guide.location && (
                          <div className="flex items-center text-sm text-gray-600">
                            <_MapPin className="w-4 h-4 mr-1" />
                            <span>{guide.location}</span>
                          </div>
                        )}
                        {guide.experience_count > 0 && (
                          <div className="flex items-center text-sm text-gray-600">
                            <Calendar className="w-4 h-4 mr-1" />
                            <span>{guide.experience_count} experiences</span>
                          </div>
                        )}
                      </div>

                      {/* Bio Preview */}
                      {guide.bio && (
                        <p className="text-sm text-gray-600 line-clamp-2 mb-4">
                          {guide.bio}
                        </p>
                      )}

                      {/* Actions */}
                      <div className="flex space-x-2">
                        <button
                          onClick={() => navigate(`/guides/${guide.guide_id}`)}
                          className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                        >
                          View Profile
                        </button>
                        <button
                          onClick={() => navigate(`/customer/messages?guide=${guide.guide_id}`)}
                          className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 transition-colors"
                          title="Send message"
                        >
                          <MessageSquare className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Favorite Experiences Tab */}
        {EXPERIENCE_FAVORITES_ENABLED && activeTab === 'experiences' && (
          <div
            role="tabpanel"
            id="experiences-panel"
            aria-labelledby="experiences-tab"
          >
            {favoriteExperiences.length === 0 ? (
              <EmptyState
                icon={Heart}
                title="No Favorite Experiences Yet"
                description="Start exploring and save experiences you're interested in"
                action={
                  <Button onClick={() => navigate('/')}>
                    Browse Experiences
                  </Button>
                }
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {favoriteExperiences.map((experience) => (
                  <div
                    key={experience.experience_id}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                  >
                    {/* Experience Image */}
                    <div className="relative">
                      {experience.primary_photo_url ? (
                        <img
                          src={experience.primary_photo_url}
                          srcSet={buildSrcSet(experience.variants) || undefined}
                          sizes={buildSrcSet(experience.variants) ? getResponsiveSizes('card') : undefined}
                          alt={experience.title}
                          className="w-full h-48 object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-48 bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                          <Calendar className="w-16 h-16 text-white opacity-50" />
                        </div>
                      )}
                      <button
                        onClick={() => setConfirmModal({ isOpen: true, type: 'experiences', id: experience.experience_id })}
                        disabled={removingId === experience.experience_id}
                        className="absolute top-3 right-3 p-2 bg-white text-red-500 rounded-full shadow-md hover:bg-red-50 transition-colors"
                        title="Remove from favorites"
                      >
                        {removingId === experience.experience_id ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-500"></div>
                        ) : (
                          <Heart className="w-5 h-5 fill-current" />
                        )}
                      </button>
                    </div>

                    {/* Experience Details */}
                    <div className="p-6">
                      <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                        {experience.title}
                      </h3>

                      {/* Guide Info */}
                      <div className="flex items-center text-sm text-gray-600 mb-3">
                        <_User className="w-4 h-4 mr-1" />
                        <span>with {experience.guide_name}</span>
                      </div>

                      {/* Experience Stats */}
                      <div className="space-y-2 mb-4">
                        {experience.average_rating && (
                          <div className="flex items-center text-sm text-gray-600">
                            <_Star className="w-4 h-4 text-yellow-400 fill-current mr-1" />
                            <span className="font-medium text-gray-900">{experience.average_rating.toFixed(1)}</span>
                            <span className="mx-1">·</span>
                            <span>{experience.review_count} reviews</span>
                          </div>
                        )}
                        {experience.location && (
                          <div className="flex items-center text-sm text-gray-600">
                            <_MapPin className="w-4 h-4 mr-1" />
                            <span>{experience.location}</span>
                          </div>
                        )}
                        {experience.price_per_person_cents && (
                          <div className="flex items-center text-sm font-semibold text-gray-900">
                            <DollarSign className="w-4 h-4 mr-1" />
                            <span>{formatCentsToDollars(experience.price_per_person_cents)} per person</span>
                          </div>
                        )}
                      </div>

                      {/* Description Preview */}
                      {experience.description && (
                        <p className="text-sm text-gray-600 line-clamp-2 mb-4">
                          {experience.description}
                        </p>
                      )}

                      {/* Actions */}
                      <div className="flex space-x-2">
                        <button
                          onClick={() => navigate(`/experience/${experience.experience_id}`)}
                          className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                        >
                          View Details
                        </button>
                        <button
                          onClick={() => navigate(`/guides/${experience.guide_id}`)}
                          className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 transition-colors"
                          title="View guide profile"
                        >
                          <_User className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, type: null, id: null })}
        onConfirm={() => handleRemoveFavorite(confirmModal.type, confirmModal.id)}
        title="Remove Favorite"
        message={`Remove this ${confirmModal.type === 'guides' ? 'guide' : 'experience'} from your favorites?`}
        confirmText="Remove"
        cancelText="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </DashboardLayout>
  );
}
